// hooks/use-session-countdown.ts
// Spec 0015, extended: the shared tick logic behind both the sidebar's
// visible countdown and the full screen expiry modal, so the two can never
// disagree about how much time is left or drift out of sync with each
// other. Reads session.sessionExpiresAt (the real, fixed deadline set once
// at sign in, never extended) rather than keeping its own accumulating
// counter, so a backgrounded tab (browsers throttle setInterval there) or a
// drifted device clock self corrects on the next tick instead of showing a
// stale number, and a page refresh picks the exact same deadline back up
// immediately (no local state to lose).
//
// Cost: one setInterval(1000) doing a single Date.now() subtraction per
// consumer. Each mounted consumer runs its own tick (no shared singleton),
// which keeps this hook simple and stateless; at the one or two places this
// is actually mounted (the sidebar, the expiry modal) that is a trivial,
// unmeasurable amount of work, not a background thread and not a source of
// growing memory: the interval and the visibilitychange listener are both
// torn down on unmount, every render.
"use client";

import { useSession } from "next-auth/react";
import { useEffect, useState } from "react";

export function useSessionCountdown() {
  const { data: session, status } = useSession();
  const sessionExpiresAt = session?.sessionExpiresAt;

  const [remainingMs, setRemainingMs] = useState<number | null>(null);

  useEffect(() => {
    if (status !== "authenticated" || !sessionExpiresAt) {
      setRemainingMs(null);
      return;
    }

    const expiresAtMs = new Date(sessionExpiresAt).getTime();
    const tick = () => setRemainingMs(expiresAtMs - Date.now());

    tick();
    const interval = setInterval(tick, 1000);

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") tick();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [status, sessionExpiresAt]);

  return {
    // null: not authenticated / not yet known. A negative number: expired.
    remainingMs,
    expired: remainingMs !== null && remainingMs <= 0,
  };
}
